import React, { useState, useEffect, useCallback, useRef } from 'react';
import useCurrentPath from 'frontend/hooks/useCurrentPath';
import { ROUTE_KEYS } from 'frontend/constants';
import store from '../../../../storeClient';
import LoadSpinner from 'frontend/commons/spinner';

const BillboardImage = ({ image = '', pathname = '', routesToRenderFullPackageSearch = [] }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const mountedRef = useRef(true);
  const imageLoadedRef = useRef(false);
  const shouldRenderFullHeight = routesToRenderFullPackageSearch.includes(pathname);
  const MAX_RETRIES = 3;

  const getHeaderImagen = useCallback(() => {
    try {
      const state = store.getState();
      return state?.applicationState?.templateData?.headerImagen?.[0]?.valor || null;
    } catch (error) {
      return null;
    }
  }, []);

  const headerImagen = getHeaderImagen();
  const imageUrl = headerImagen || image;
  const { isOnTargetPath } = useCurrentPath([ROUTE_KEYS.HOME, ROUTE_KEYS.BUSCAR_X_PAQUETES]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!mountedRef.current) return;

    if (headerImagen && !imageLoadedRef.current) {
      setIsLoading(true);
      setImageError(false);
      setRetryCount(0); // Reset retry count when headerImagen changes
    } else if (!headerImagen) {
      setIsLoading(false);
      setImageError(true);
    }
  }, [headerImagen]);

  const handleImageLoad = useCallback(() => {
    if (!mountedRef.current) return;
    imageLoadedRef.current = true;
    setIsLoading(false);
    setImageError(false);
    setRetryCount(0); // Reset retry count on successful load
  }, []);

  const handleImageError = useCallback(() => {
    if (!mountedRef.current) return;

    if (retryCount < MAX_RETRIES) {
      // Retry loading the image
      setRetryCount(prev => prev + 1);
      setIsLoading(true);
      setImageError(false);
      
      // Force image reload
      const img = new Image();
      img.src = imageUrl;
      img.onload = handleImageLoad;
      img.onerror = handleImageError;
    } else {
      // Max retries reached, fallback to default image
      setImageError(true);
      setIsLoading(false);
      imageLoadedRef.current = false;
    }
  }, [retryCount, imageUrl, handleImageLoad]);

  return (
    <div className="billboard__container" style={{ aspectRatio: '16/9' }}>
      {isLoading && (
        <div
          className="loading-container"
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: '#f5f5f5',
          }}
        >
          <LoadSpinner />
        </div>
      )}
      <img
        className={`background__billboard ${
          shouldRenderFullHeight ? 'full-height' : 'partial-height'
        }`}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: isLoading ? 'none' : 'block',
        }}
        src={imageError ? image : imageUrl}
        alt="Billboard background"
        onLoad={handleImageLoad}
        onError={handleImageError}
      />
    </div>
  );
};

export default BillboardImage;
